/**
 * The stateful half of manual job attribution (ENG-2440): search state, the
 * debounce ahead of the request, and the match submission. Mounted only while
 * the dialog is open — see `manual-job-match.tsx` — so each open starts from an
 * empty search rather than the last one's leftovers.
 */
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { DialogFooter } from "@volca/ui/components/ui/dialog";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@volca/ui/components/ui/toggle-group";
import { toast } from "@volca/ui/lib/toast";
import type {
  ApMatchTarget,
  useMatchDocumentJob,
} from "@/accounts-payable/query";
import { useSearchJobs, useSearchProjects } from "@/accounts-payable/query";
import type {
  ApDocumentType,
  ApJobSearchResult,
  ApProjectSearchResult,
} from "@/accounts-payable/types";
import {
  documentTypeLabel,
  isJobSearchRateLimited,
} from "@/accounts-payable/utils";
import { getErrorMessage } from "@/core/api/errors";
import { useDebounce } from "@/core/hooks";
import { JobSearchCombobox } from "./job-search-combobox";
import { ProjectSearchCombobox } from "./project-search-combobox";

/**
 * Debounce ahead of the search request — one request per pause, not per key.
 * Each request costs a fan-out of ServiceTitan calls against a per-tenant rate
 * limit shared with background sync and payroll, so this leans slow.
 */
const SEARCH_DEBOUNCE_MS = 500;

/**
 * What the search looks for. Most documents belong to a job; some businesses
 * file some work (installs, say) on the ServiceTitan project itself, with no job.
 */
type MatchMode = ApMatchTarget["kind"];

const MODE_LABELS: Record<MatchMode, string> = {
  job: "Job",
  project: "Project",
};

const targetLabel = (target: ApMatchTarget) =>
  target.kind === "job"
    ? `job ${target.job.job_number}`
    : `project ${target.project.number}`;

export const ManualJobMatchForm = ({
  businessId,
  documentId,
  documentType = "invoice",
  agentRunId,
  matchJob,
  onClose,
}: {
  businessId: string | null;
  documentId: string;
  documentType?: ApDocumentType;
  agentRunId: string | null;
  /** Owned by the button above, so dismissing the dialog can't orphan it. */
  matchJob: ReturnType<typeof useMatchDocumentJob>;
  onClose: () => void;
}) => {
  const kind = documentTypeLabel(documentType);
  const [mode, setMode] = useState<MatchMode>("job");
  const [query, setQuery] = useState("");
  const [selectedJob, setSelectedJob] = useState<ApJobSearchResult | null>(
    null,
  );
  const [selectedProject, setSelectedProject] =
    useState<ApProjectSearchResult | null>(null);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);

  /**
   * A selection belongs to the results it was picked out of. Editing the
   * search text keeps the old pick selected and the confirm button live, so
   * confirming mid-search could attribute a job that the current results
   * don't even list. Trimmed, so trailing whitespace isn't a "new" search.
   */
  const onQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim() !== query.trim()) {
      setSelectedJob(null);
      setSelectedProject(null);
    }
  };
  /** A new mode is a new search: job numbers aren't project numbers. */
  const onModeChange = (next: MatchMode) => {
    setMode(next);
    setQuery("");
    setSelectedJob(null);
    setSelectedProject(null);
  };

  // Only the visible mode searches; the other gets a query too short to send.
  const jobSearch = useSearchJobs(
    businessId,
    mode === "job" ? debouncedQuery : "",
  );
  const projectSearch = useSearchProjects(
    businessId,
    mode === "project" ? debouncedQuery : "",
  );
  const search = mode === "job" ? jobSearch : projectSearch;

  /**
   * Keystrokes still waiting on the debounce count as loading: the results on
   * screen answer `debouncedQuery`, so without this the gap between the two
   * renders a premature "No jobs match that search." Placeholder data counts
   * too — those are the *previous* query's results, kept only to avoid a
   * flash, and selecting one would match the wrong job.
   */
  const isSearchPending =
    search.isLoading || search.isPlaceholderData || query !== debouncedQuery;

  const target: ApMatchTarget | null =
    mode === "job"
      ? selectedJob && { kind: "job", job: selectedJob }
      : selectedProject && { kind: "project", project: selectedProject };

  const confirm = () => {
    if (!target) return;
    matchJob.mutate(
      { documentId, target },
      {
        onSuccess: (document) => {
          const agentStarted =
            document.agent_run_id !== null &&
            document.agent_run_id !== agentRunId;
          toast.success(
            agentStarted
              ? `${kind.capitalized} matched to ${targetLabel(target)}. The agent is running against the new match.`
              : `${kind.capitalized} matched to ${targetLabel(target)}.`,
          );
          onClose();
        },
        onError: (error: unknown) =>
          toast.error(`Couldn’t match the ${kind.lower} to that ${mode}`, {
            description: getErrorMessage(error),
          }),
      },
    );
  };

  return (
    <>
      <Stack align="start" gap={4} className="w-full">
        <ToggleGroup
          size="sm"
          variant="outline"
          value={[mode]}
          onValueChange={(next) => {
            const picked = (["job", "project"] as const).find(
              (value) => value === next[0],
            );
            if (picked) onModeChange(picked);
          }}
          disabled={matchJob.isPending}
          aria-label="Match to a job or a project"
        >
          {(["job", "project"] as const).map((value) => (
            <ToggleGroupItem key={value} value={value}>
              {MODE_LABELS[value]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {mode === "job" ? (
          <JobSearchCombobox
            query={query}
            onQueryChange={onQueryChange}
            jobs={jobSearch.data ?? []}
            isLoading={isSearchPending}
            isFetching={jobSearch.isFetching}
            isError={jobSearch.isError}
            isRateLimited={isJobSearchRateLimited(jobSearch.error)}
            selectedJob={selectedJob}
            onSelect={setSelectedJob}
            disabled={matchJob.isPending}
          />
        ) : (
          <ProjectSearchCombobox
            query={query}
            onQueryChange={onQueryChange}
            projects={projectSearch.data ?? []}
            isLoading={isSearchPending}
            isFetching={projectSearch.isFetching}
            isError={projectSearch.isError}
            isRateLimited={isJobSearchRateLimited(projectSearch.error)}
            selectedProject={selectedProject}
            onSelect={setSelectedProject}
            disabled={matchJob.isPending}
          />
        )}
      </Stack>
      <DialogFooter>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={matchJob.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={confirm}
          disabled={!target || matchJob.isPending}
          aria-busy={matchJob.isPending || undefined}
        >
          {matchJob.isPending && (
            <Spinner aria-hidden data-icon="inline-start" />
          )}
          {target ? `Match to ${targetLabel(target)}` : `Match to ${mode}`}
        </Button>
      </DialogFooter>
    </>
  );
};
